import _ from "lodash";
/** External */
import { graphql } from "lib/apolloClient";
import update from "immutability-helper";

/** Internal */
import {
  getOverviewMetricsQuery,
  getTableMetricsQuery,
  getGraphDataQuery,
  getAnalyticsEntityQuery,
  getLastSyncTimeQuery,
} from "./ToddleAnalyticsQueries";
import {
  getOverviewMetricsFromCache,
  getTableMetricsFromCache,
  getGraphDataFromCache,
} from "./ToddleAnalyticsGraphqlHelpers";
import { prepareLoadMoreFeed } from "Utils";
import { isGraphqlQueryLoading } from "./Utils";

export const getOverviewMetricsQueryEnhancer = graphql(
  getOverviewMetricsQuery,
  {
    name: "getOverviewMetrics",
    skip: ({ skipOverviewQuery }) => skipOverviewQuery,
    options: ({ overviewMetricsQueryVariables }) => ({
      fetchPolicy: "cache-and-network",
      variables: overviewMetricsQueryVariables,
    }),
    props: ({
      getOverviewMetrics: { variables, networkStatus },
      ownProps: { isData, isLoading },
    }) => {
      const queryData = getOverviewMetricsFromCache(variables);
      const overviewMetrics = _.get(queryData, "analytics.overviewMetrics");

      return {
        overviewMetrics,
        isOverviewMetricsDataInCache: !_.isUndefined(overviewMetrics),
        isLoading: isGraphqlQueryLoading(networkStatus) || isLoading,
        isData: !_.isEmpty(queryData) && isData,
      };
    },
  }
);

export const getTableMetricsQueryEnhancer = graphql(getTableMetricsQuery, {
  name: "getTableMetrics",
  skip: ({ shouldSkipTableQuery }) => shouldSkipTableQuery,
  options: ({ tableMetricsQueryVariables }) => ({
    fetchPolicy: "cache-and-network",
    variables: tableMetricsQueryVariables,
  }),
  props: ({ getTableMetrics, ownProps: { isData, isLoading } }) => {
    const { variables, networkStatus } = getTableMetrics;
    const queryData = getTableMetricsFromCache(variables);
    const individualMetrics = _.get(
      queryData,
      "analytics.individualMetrics",
      {}
    );

    const totalCount = _.get(individualMetrics, "totalCount", 0);
    const hasNextPage = _.get(individualMetrics, "pageInfo.hasNextPage");
    const endCursor = _.get(individualMetrics, "pageInfo.endCursor", "");
    const tableMetrics = _.get(individualMetrics, "edges");

    const updatedVariables = update(variables, {
      tableMetricFilters: {
        paginationFilters: {
          $merge: {
            after: endCursor,
          },
        },
      },
    });

    return {
      totalCount,
      tableMetrics,
      isTableMetricsDataInCache: !_.isUndefined(tableMetrics),
      isLoading: isGraphqlQueryLoading(networkStatus) || isLoading,
      isData: !_.isEmpty(queryData) && isData,
      hasNextPage,
      networkStatus,
      loadMoreFeed: prepareLoadMoreFeed({
        query: getTableMetricsQuery,
        variables: { ...updatedVariables },
        queryData: getTableMetrics,
        customPathString: "node.analytics.individualMetrics",
      }),
    };
  },
});

export const getGraphDataQueryEnhancer = graphql(getGraphDataQuery, {
  name: "getGraphData",
  skip: ({ skipGraphQuery }) => skipGraphQuery,
  options: ({ graphDataQueryVariables }) => ({
    fetchPolicy: "cache-and-network",
    variables: graphDataQueryVariables,
  }),
  props: ({
    getGraphData: { variables, networkStatus },
    ownProps: { isData, isLoading },
  }) => {
    const queryData = getGraphDataFromCache(variables);
    const graphData = _.get(queryData, "analytics.graphData");

    return {
      graphData,
      isGraphDataInCache: !_.isUndefined(graphData),
      isLoading: isGraphqlQueryLoading(networkStatus) || isLoading,
      isData: !_.isEmpty(queryData) && isData,
    };
  },
});

export const getAnalyticsEntityQueryEnhancer = ({
  queryVariablesKey,
  dataPropName = "analyticsEntityData",
  skip = _.stubFalse,
}) =>
  graphql(getAnalyticsEntityQuery, {
    name: dataPropName,
    options: props => ({
      fetchPolicy: "cache-and-network",
      variables: props[queryVariablesKey],
    }),
    skip,
    props: ({
      [dataPropName]: getAnalyticsEntityData,
      ownProps: { isData, isLoading },
    }) => {
      const { node, networkStatus, variables } = getAnalyticsEntityData;
      const endCursor = _.get(
        node,
        "analytics.analyticsEntityData.pageInfo.endCursor"
      );
      const paginationFilters = _.get(
        variables,
        "analyticsEntityFilterInput.paginationFilters",
        {}
      );

      const updatedVariables = !_.isEmpty(paginationFilters)
        ? update(variables, {
            analyticsEntityFilterInput: {
              paginationFilters: {
                $merge: {
                  after: endCursor,
                },
              },
            },
          })
        : variables;

      return {
        [dataPropName]: _.get(node, "analytics.analyticsEntityData"),
        [`${dataPropName}AdditionalProps`]: {
          networkStatus,
          loadMore: prepareLoadMoreFeed({
            query: getAnalyticsEntityQuery,
            variables: updatedVariables,
            queryData: getAnalyticsEntityData,
            customPathString: "node.analytics.analyticsEntityData",
          }),
        },
        isLoading: isLoading || _.includes([1, 2], networkStatus),
        isData: !_.isEmpty(node) && isData,
      };
    },
  });

export const getLastSyncTimeQueryEnhancer = ({
  queryVariablesKey = "lastSyncTimeQueryVariables",
}) =>
  graphql(getLastSyncTimeQuery, {
    name: "getLastSyncTime",
    options: props => ({
      fetchPolicy: "cache-and-network",
      variables: props[queryVariablesKey],
    }),
    props: ({
      getLastSyncTime: { node, networkStatus },
      ownProps: { isData, isLoading },
    }) => ({
      isLoading: isGraphqlQueryLoading(networkStatus) || isLoading,
      isData: !_.isEmpty(node) && isData,
    }),
  });
